import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Lock,
  Download,
  X,
  ArrowLeft,
  Loader2,
  Search,
  ArrowRight,
  Heart,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Peek = () => {
  const { slug: slugParam } = useParams();
  const navigate = useNavigate();

  const [slug, setSlug] = useState(slugParam || "");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [gallery, setGallery] = useState(null);
  const [active, setActive] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [favBusy, setFavBusy] = useState(null);

  const loadFavorites = useCallback(async (s, c) => {
    try {
      const r = await axios.get(
        `${API}/peek/${s}/favorites?code=${encodeURIComponent(c)}`
      );
      setFavorites(new Set(r.data.photo_ids || []));
    } catch (err) {
      // non-fatal
    }
  }, []);

  const unlock = useCallback(
    async (s, c) => {
      setBusy(true);
      setError("");
      try {
        const r = await axios.post(`${API}/peek/${s}/unlock`, { code: c });
        setGallery(r.data);
        sessionStorage.setItem(`peek:${s}`, c);
        await loadFavorites(s, c);
        if (s !== slugParam) navigate(`/peek/${s}`, { replace: true });
      } catch (err) {
        setError(err?.response?.data?.detail || "Wrong code or gallery.");
      } finally {
        setBusy(false);
      }
    },
    [slugParam, navigate, loadFavorites]
  );

  // If accessed via /peek/:slug directly with a stored code in sessionStorage, auto-unlock
  useEffect(() => {
    if (slugParam) {
      const cached = sessionStorage.getItem(`peek:${slugParam}`);
      if (cached) {
        setCode(cached);
        unlock(slugParam, cached);
      }
    }
  }, [slugParam, unlock]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!slug.trim() || !code.trim()) return;
    unlock(slug.trim().toLowerCase(), code.trim());
  };

  const photoUrl = (p, download = false) =>
    `${API}/peek/${gallery.slug}/photo/${p.id}?code=${encodeURIComponent(code)}${download ? "&download=1" : ""}`;

  const downloadAll = async () => {
    setZipping(true);
    try {
      const r = await axios.get(
        `${API}/peek/${gallery.slug}/zip?code=${encodeURIComponent(code)}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${gallery.slug}-manevzion.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("Could not prepare zip.");
    } finally {
      setZipping(false);
    }
  };

  const lockOut = () => {
    if (gallery) sessionStorage.removeItem(`peek:${gallery.slug}`);
    setGallery(null);
    setCode("");
    setFavorites(new Set());
  };

  const toggleFavorite = async (photoId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!gallery || favBusy === photoId) return;
    setFavBusy(photoId);
    // Optimistic update
    const wasFav = favorites.has(photoId);
    setFavorites((prev) => {
      const next = new Set(prev);
      if (wasFav) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
    try {
      await axios.post(
        `${API}/peek/${gallery.slug}/favorites/${photoId}`,
        { code }
      );
    } catch (err) {
      // Revert on failure
      setFavorites((prev) => {
        const next = new Set(prev);
        if (wasFav) next.add(photoId);
        else next.delete(photoId);
        return next;
      });
    } finally {
      setFavBusy(null);
    }
  };

  // ---------- Locked state (form) ----------
  if (!gallery) {
    return (
      <main
        data-testid="peek-locked"
        className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6"
      >
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate("/")}
            className="overline link-underline mb-8 inline-flex items-center gap-2 text-[#5C5C5C]"
            data-testid="peek-back"
          >
            <ArrowLeft size={14} strokeWidth={1.4} /> Back to site
          </button>
          <div className="border border-[#E6E2DA] bg-white p-10">
            <div className="flex items-center gap-3 mb-2">
              <Lock size={18} strokeWidth={1.2} className="text-[#C48B71]" />
              <p className="overline">Client Gallery</p>
            </div>
            <h1 className="serif text-4xl font-light text-[#2A2A2A] leading-tight">
              Welcome to your <span className="italic text-[#C48B71]">sneak peek</span>.
            </h1>
            <p className="text-[#5C5C5C] text-sm mt-3 font-light">
              Enter your gallery name and access code from your photographer.
            </p>

            <form onSubmit={onSubmit} className="mt-8" data-testid="peek-form">
              <label className="overline block mb-1">Gallery name</label>
              <div className="flex items-center">
                <span className="text-[#5C5C5C] text-sm mr-1 font-light">/peek/</span>
                <input
                  required
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="maya-jordan-2026"
                  data-testid="peek-slug"
                  className="input-editorial flex-1"
                />
              </div>

              <label className="overline block mt-6 mb-1">Access code</label>
              <input
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="MAYA2026"
                data-testid="peek-code"
                className="input-editorial"
              />

              {error && (
                <p data-testid="peek-error" className="overline text-red-700 mt-3">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="peek-submit"
                className="btn-primary mt-8 w-full justify-center disabled:opacity-60"
              >
                {busy ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Search size={16} strokeWidth={1.4} />
                )}
                Unlock gallery
              </button>
            </form>
          </div>
        </div>
      </main>
    );
  }

  // ---------- Unlocked gallery ----------
  return (
    <main
      data-testid="peek-unlocked"
      className="min-h-screen bg-[#F9F8F6] px-6 md:px-10 lg:px-16 py-12"
    >
      <div className="mx-auto max-w-[1500px]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 border-b border-[#E6E2DA] pb-8">
          <div>
            <p className="overline">Sneak Peek · {gallery.client_name}</p>
            <h1 className="serif text-4xl md:text-6xl font-light text-[#2A2A2A] mt-3 leading-[1.05]">
              {gallery.title}
            </h1>
            <p className="text-[#5C5C5C] text-sm mt-3 font-light">
              {gallery.photos.length} photos · For your eyes only
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={downloadAll}
              disabled={zipping || gallery.photos.length === 0}
              data-testid="peek-download-all"
              className="btn-primary disabled:opacity-60"
            >
              {zipping ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} strokeWidth={1.4} />
              )}
              {zipping ? "Preparing…" : "Download all (ZIP)"}
            </button>
            <button onClick={lockOut} className="btn-outline" data-testid="peek-lock">
              <Lock size={14} strokeWidth={1.4} /> Lock
            </button>
          </div>
        </div>

        {gallery.photos.length === 0 ? (
          <div className="text-center py-24">
            <p className="serif text-2xl text-[#2A2A2A]">No photos uploaded yet</p>
            <p className="overline text-[#5C5C5C] mt-2">Check back soon.</p>
          </div>
        ) : (
          <>
            {favorites.size > 0 && (
              <p
                data-testid="peek-favorites-count"
                className="overline text-[#C48B71] mb-6 -mt-2"
              >
                <Heart
                  size={12}
                  strokeWidth={1.6}
                  className="inline -mt-0.5 mr-2"
                  fill="#C48B71"
                />
                {favorites.size} favorited · Jas can see your picks
              </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {gallery.photos.map((p, i) => {
                const isFav = favorites.has(p.id);
                return (
                  <div
                    key={p.id}
                    className="img-hover relative aspect-[3/4] bg-[#E6E2DA] overflow-hidden group"
                  >
                    <button
                      onClick={() => setActive(p)}
                      data-testid={`peek-photo-${i}`}
                      className="absolute inset-0 w-full h-full"
                      aria-label={`Open ${p.filename}`}
                    >
                      <img
                        src={photoUrl(p)}
                        alt={p.filename}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    </button>
                    <button
                      onClick={(e) => toggleFavorite(p.id, e)}
                      data-testid={`peek-favorite-${i}`}
                      aria-pressed={isFav}
                      aria-label={isFav ? "Unfavorite photo" : "Favorite photo"}
                      className={`absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full transition-all ${
                        isFav
                          ? "bg-[#C48B71] text-white shadow-md"
                          : "bg-black/35 text-white opacity-0 group-hover:opacity-100 hover:bg-black/55 backdrop-blur-sm"
                      } ${favBusy === p.id ? "scale-95" : "hover:scale-110"}`}
                    >
                      <Heart
                        size={16}
                        strokeWidth={1.6}
                        fill={isFav ? "currentColor" : "transparent"}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <p className="text-center overline text-[#5C5C5C] mt-16">
          ManeVzion Photography · Please do not share this link publicly
        </p>
      </div>

      {/* Lightbox */}
      {active && (
        <div
          data-testid="peek-lightbox"
          className="fixed inset-0 z-[60] bg-[#2A2A2A]/95 flex items-center justify-center p-6"
          onClick={() => setActive(null)}
        >
          <button
            onClick={() => setActive(null)}
            data-testid="peek-lightbox-close"
            className="absolute top-6 right-6 text-[#F9F8F6] hover:text-[#C48B71]"
            aria-label="Close"
          >
            <X size={28} strokeWidth={1.2} />
          </button>
          <button
            onClick={(e) => toggleFavorite(active.id, e)}
            data-testid="peek-lightbox-favorite"
            aria-pressed={favorites.has(active.id)}
            aria-label={favorites.has(active.id) ? "Unfavorite photo" : "Favorite photo"}
            className={`absolute top-6 left-6 inline-flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
              favorites.has(active.id)
                ? "bg-[#C48B71] text-white"
                : "bg-white/10 text-[#F9F8F6] hover:bg-white/20 backdrop-blur-sm"
            }`}
          >
            <Heart
              size={16}
              strokeWidth={1.6}
              fill={favorites.has(active.id) ? "currentColor" : "transparent"}
            />
            <span className="overline">
              {favorites.has(active.id) ? "Favorited" : "Favorite"}
            </span>
          </button>
          <img
            src={photoUrl(active)}
            alt={active.filename}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <a
            href={photoUrl(active, true)}
            data-testid="peek-lightbox-download"
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-6 inline-flex items-center gap-2 overline text-[#F9F8F6] link-underline"
          >
            <Download size={14} strokeWidth={1.4} /> Download {active.filename}
            <ArrowRight size={14} strokeWidth={1.4} />
          </a>
        </div>
      )}
    </main>
  );
};

export default Peek;
